import type { UsageList } from '../../api/usage';
import styles from '../../css/usageDashboard.module.css';

const PAGE_WINDOW = 5;

function comma(n: number): string {
  return n.toLocaleString('ko-KR');
}

/** 현재 페이지 주변만 보여주고 양끝은 생략 부호로 접는다 */
function pageItems(current: number, pages: number): (number | 'gap')[] {
  const end = Math.min(pages, Math.max(1, current - 2) + PAGE_WINDOW - 1);
  const start = Math.max(1, end - PAGE_WINDOW + 1);
  const items: (number | 'gap')[] = [];

  if (start > 1) {
    items.push(1);
    if (start > 2) items.push('gap');
  }

  for (let p = start; p <= end; p++) items.push(p);

  if (end < pages) {
    if (end < pages - 1) items.push('gap');
    items.push(pages);
  }

  return items;
}

type Props = {
  data: UsageList | null;
  loading: boolean;
  error: string | null;
  onPageChange: (page: number) => void;
};

export default function UsageLogTable({ data, loading, error, onPageChange }: Props) {
  if (error) return <p className={styles.state}>{error}</p>;
  if (loading && !data) return <p className={styles.state}>불러오는 중…</p>;
  if (!data) return null;
  if (data.total === 0) return <p className={styles.state}>이 달에는 집계된 질의가 없습니다.</p>;

  const pages = Math.max(1, Math.ceil(data.total / data.size));
  const from = (data.page - 1) * data.size;

  return (
    <>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>질의 일시<span className={styles.caret}>▼</span></th>
              <th>질문</th>
              <th>답변</th>
              <th>평가</th>
              <th className={styles.right}>레이턴시</th>
              <th className={styles.right}>듀레이션</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row, i) => (
              <tr key={`${row.createdAt}-${i}`}>
                <td className={`${styles.num} ${styles.dim}`}>{row.createdAt}</td>
                <td><div className={styles.clamp}>{row.userMessage}</div></td>
                <td><div className={`${styles.clamp} ${styles.clampAnswer}`}>{row.assistantMessage}</div></td>
                <td>
                  {row.evaluation === 'LIKE' ? '👍'
                    : row.evaluation === 'DISLIKE' ? '👎'
                    : <span className={styles.dim}>–</span>}
                </td>
                <td className={`${styles.num} ${styles.dim} ${styles.right}`}>{comma(row.latency)}ms</td>
                <td className={`${styles.num} ${styles.dim} ${styles.right}`}>{comma(row.duration)}ms</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className={styles.pager}>
        <span className={styles.pagerCount}>
          전체 <b>{comma(data.total)}</b>건 중{' '}
          <b>{comma(from + 1)}–{comma(Math.min(from + data.size, data.total))}</b>
        </span>

        <div className={styles.pagerNav}>
          <button
            type="button"
            className={styles.page}
            aria-label="이전 페이지"
            disabled={data.page === 1 || loading}
            onClick={() => onPageChange(data.page - 1)}
          >
            ‹
          </button>

          {pageItems(data.page, pages).map((item, i) =>
            item === 'gap' ? (
              <button key={`gap-${i}`} type="button" className={`${styles.page} ${styles.pageGap}`} disabled>…</button>
            ) : (
              <button
                key={item}
                type="button"
                className={`${styles.page} ${item === data.page ? styles.pageCurrent : ''}`}
                aria-current={item === data.page ? 'page' : undefined}
                disabled={loading}
                onClick={() => onPageChange(item)}
              >
                {item}
              </button>
            )
          )}

          <button
            type="button"
            className={styles.page}
            aria-label="다음 페이지"
            disabled={data.page === pages || loading}
            onClick={() => onPageChange(data.page + 1)}
          >
            ›
          </button>
        </div>
      </div>
    </>
  );
}
